import { Component, Input, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import {
	ApexAxisChartSeries,
	ApexChart,
	ApexDataLabels,
	ApexMarkers,
	ApexStroke,
	ApexTitleSubtitle,
	ApexTooltip,
	ApexXAxis,
	ApexYAxis,
	NgApexchartsModule
} from 'ng-apexcharts';

import { SimChartContext } from 'sim-management-frontend/dist/sim-management';

/** Most day labels the x axis prints; at 390px wide, more than this overlap. */
const MAX_DAY_LABELS = 8;

/**
 * Draws the usage-history chart for the `sim-management` library.
 *
 * The library stopped drawing charts itself — `angular-google-charts` has no Angular 21 build —
 * and now only says what to draw, through {@link SimChartContext}. VMSPRO already has
 * ApexCharts, so this component is registered as the library's `CHART_RENDERER` and gets the
 * context as its only input.
 *
 * The context carries `data` as `[label, value]` pairs, already in KB and already in day order,
 * and `columnNames` as `[x axis name, series name]`.
 *
 * `context` is a setter rather than an `ngOnChanges` hook on purpose: the library creates this
 * component through a `ViewContainerRef` and then assigns `instance.context = ...` directly.
 * A plain property write outside a template binding never runs change detection's input
 * machinery, so `ngOnChanges` would never fire and the chart would stay empty.
 */
@Component({
	selector: 'app-sim-usage-chart',
	standalone: true,
	imports: [CommonModule, NgApexchartsModule],
	template: `
		@if (hasData) {
			<apx-chart
				[series]="series"
				[chart]="chart"
				[xaxis]="xaxis"
				[yaxis]="yaxis"
				[stroke]="stroke"
				[markers]="markers"
				[dataLabels]="dataLabels"
				[tooltip]="tooltip"
				[title]="titleOptions"
			></apx-chart>
		}
	`
})
export class SimUsageChartComponent {
	private readonly translate = inject(TranslateService);

	private _context: SimChartContext;

	@Input()
	set context(value: SimChartContext) {
		this._context = value;
		this.buildChart();
	}

	get context(): SimChartContext {
		return this._context;
	}

	series: ApexAxisChartSeries = [];
	chart: ApexChart;
	xaxis: ApexXAxis;
	yaxis: ApexYAxis;
	stroke: ApexStroke;
	markers: ApexMarkers;
	dataLabels: ApexDataLabels;
	tooltip: ApexTooltip;
	titleOptions: ApexTitleSubtitle;
	hasData = false;

	private buildChart() {
		const context = this._context;
		const rows = Array.isArray(context?.data) ? context.data : [];

		this.hasData = rows.length > 0;
		if (!this.hasData) {
			this.series = [];
			return;
		}

		const categories = rows.map((row) => row?.[0]);
		const values = rows.map((row) => Number(row?.[1]) || 0);

		// The values are KB — the library divides by 1024 — but its series name
		// (`columnNames[1]`) is a bare "Data Usage". Name the unit, as the old screen did
		// (VMSPRO-591).
		const seriesName = this.translate.instant('sim_management.data_usage_kb');

		this.series = [{ name: seriesName, data: values }];

		this.chart = {
			type: 'line',
			height: context?.height || 300,
			toolbar: { show: false },
			zoom: { enabled: false }
		};

		// A month of days on a phone-width dialog printed every label and they ran together
		// (VMSPRO-593). `tickAmount` caps how many day labels the axis prints; ApexCharts spaces
		// them evenly, and the tooltip still names every point.
		this.xaxis = {
			categories,
			type: 'category',
			tickAmount: Math.min(categories.length, MAX_DAY_LABELS),
			labels: { rotate: -45, hideOverlappingLabels: true }
		};

		this.yaxis = {
			title: { text: seriesName }
		};

		this.stroke = { curve: 'straight', width: 2 };
		this.markers = { size: 4 };
		this.dataLabels = { enabled: false };
		this.tooltip = { shared: false };
		this.titleOptions = { text: context?.title ?? '' };
	}
}
